import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const isSupabaseAvailable = !!(supabaseUrl && supabaseAnonKey);
export { isSupabaseAvailable };

export const supabase = isSupabaseAvailable
  ? createClient(supabaseUrl, supabaseAnonKey)
  : (null as any);

export interface CMSContent {
  id?: number;
  page_id: string;
  type: string;
  data: any;
  order: number;
  status: 'published' | 'draft' | 'private';
  private_link?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Statistic {
  id?: number;
  type: 'recent' | 'best';
  success_rate: string;
  total_signals: number;
  total_profit: string;
  profit_ratio: string;
  month: string;
  created_at?: string;
  updated_at?: string;
}

export interface Partner {
  id?: number;
  name: string;
  logo_url: string;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Fetch contents by page ID
export async function fetchContentsByPage(pageId: string, status?: 'published' | 'draft' | 'private') {
  if (!isSupabaseAvailable) return [];
  try {
    let query = supabase
      .from('cms_contents')
      .select('*')
      .eq('page_id', pageId)
      .order('order', { ascending: true });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// Fetch all published contents for public pages
export async function fetchPublishedContents(pageId: string) {
  if (!isSupabaseAvailable) return [];
  try {
    const { data, error } = await supabase
      .from('cms_contents')
      .select('*')
      .eq('page_id', pageId)
      .eq('status', 'published')
      .order('order', { ascending: true });

    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// Create new content
export async function createContent(content: CMSContent) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('cms_contents')
    .insert([{
      page_id: content.page_id,
      type: content.type,
      data: content.data,
      order: content.order,
      status: content.status,
      private_link: content.private_link
    }])
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Update content
export async function updateContent(id: number, updates: Partial<CMSContent>) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('cms_contents')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Delete content
export async function deleteContent(id: number) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { error } = await supabase
    .from('cms_contents')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
}

// Statistics functions
export async function fetchStatistics() {
  if (!isSupabaseAvailable) return [];
  try {
    const { data, error } = await supabase
      .from('statistics')
      .select('*')
      .order('type', { ascending: true });

    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

export async function updateStatistic(id: number, updates: Partial<Statistic>) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('statistics')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Partners functions
export async function fetchPartners(activeOnly = true) {
  if (!isSupabaseAvailable) return [];
  try {
    let query = supabase
      .from('partners')
      .select('*')
      .order('display_order', { ascending: true });

    if (activeOnly) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

export async function createPartner(partner: Omit<Partner, 'id' | 'created_at' | 'updated_at'>) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('partners')
    .insert([partner])
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updatePartner(id: number, updates: Partial<Partner>) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('partners')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function deletePartner(id: number) {
  if (!isSupabaseAvailable) throw new Error('Supabase not available');
  const { error } = await supabase
    .from('partners')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
}

// Helper function to get contents by page and type
export async function fetchContentsByPageAndType(pageId: string, type?: string) {
  if (!isSupabaseAvailable) return [];
  try {
    let query = supabase
      .from('cms_contents')
      .select('*')
      .eq('page_id', pageId)
      .eq('status', 'published')
      .order('order', { ascending: true });

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}